import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { UsersRound } from 'lucide-react';
import { PageHeader, Breadcrumbs, BreadcrumbItem } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Button,
  Badge,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { SettingRow } from '@vnetwork/ui/layout';
import { StatusPill, EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useContacts,
  useOrgMe,
  useOrgRole,
  useOrgAdmins,
  useProvisionLogin,
  useSetPrimaryContact,
  useDeleteContact,
  orgErrorMessage,
} from '@/api/organization';
import { ContactDialog } from '@/pages/account/organization/components/ContactDialog';
import { DescList, DescItem } from '@/pages/services/components/DescList';
import { ORG_BASE } from '../organizations-service';
import { findContactByRef } from '@/lib/contact-ref';
import { memberOrgRole, memberRoleTone, accountState } from './member-model';
import styles from './PersonDetail.module.css';

const MEMBERS_ROUTE = `${ORG_BASE}/members`;

/** Disabled lifecycle control with a "Coming soon" hint. Reset-password (admin)
 *  and Require-2FA are @datacentral Phase-2 deltas; Disable/Enable endpoints
 *  exist but their admin-targets-member contract is unverified — gated until
 *  confirmed rather than guess-wiring a destructive account action. */
function GatedAction({ label }: { label: string }) {
  return (
    <div className={styles.gatedAction}>
      <Button variant="secondary" size="sm" disabled>
        {label}
      </Button>
      <span className={styles.comingSoon}>Coming soon</span>
    </div>
  );
}

/**
 * Per-person detail page (`members/:personRef`). The single home for a member's
 * account, details, org access and removal. The route param is an opaque UUIDv5
 * ref (see lib/contact-ref) resolved ONLY against the caller's own org contacts,
 * so raw DataCentral ids never appear in the URL and other orgs' refs never
 * resolve. Wires the existing safe endpoints; lifecycle actions needing backend
 * deltas or contract verification are gated.
 */
export function PersonDetailPage() {
  const { personRef } = useParams();
  const navigate = useNavigate();
  const { show } = useToast();
  const { data: me } = useOrgMe();
  const { canManage } = useOrgRole();

  const { data, isLoading } = useContacts({ page: 1, pageSize: 100 });
  const { data: admins = [] } = useOrgAdmins({ enabled: canManage });

  const contact =
    me && personRef ? findContactByRef(data?.results ?? [], me.customer_id, personRef) : undefined;

  const [editOpen, setEditOpen] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [removing, setRemoving] = useState(false);

  const provision = useProvisionLogin({
    onSuccess: () => {
      show({ tone: 'success', title: 'Account created', description: 'A set-password link was emailed.' });
      setProvisioning(false);
    },
    onError: (err) => show({ tone: 'danger', title: orgErrorMessage(err, 'Could not create account') }),
  });

  const setPrimary = useSetPrimaryContact({
    onSuccess: () => show({ tone: 'success', title: 'Primary contact updated' }),
    onError: (err) => show({ tone: 'danger', title: orgErrorMessage(err, 'Could not set primary contact') }),
  });

  const remove = useDeleteContact({
    onSuccess: () => {
      show({ tone: 'success', title: 'Member removed' });
      navigate(MEMBERS_ROUTE);
    },
    onError: (err) => show({ tone: 'danger', title: orgErrorMessage(err, 'Could not remove member') }),
  });

  const crumbs = (
    <Breadcrumbs>
      <BreadcrumbItem asChild>
        <Link to={MEMBERS_ROUTE}>Members</Link>
      </BreadcrumbItem>
      <BreadcrumbItem current>{contact?.contact_name || 'Member'}</BreadcrumbItem>
    </Breadcrumbs>
  );

  if (isLoading) {
    return (
      <>
        <PageHeader breadcrumbs={crumbs} icon={<UsersRound size={22} />} title="Member" />
        <div className={styles.stack}>
          <Skeleton style={{ height: 120 }} />
          <Skeleton style={{ height: 200 }} />
        </div>
      </>
    );
  }

  if (!contact) {
    return (
      <>
        <PageHeader breadcrumbs={crumbs} icon={<UsersRound size={22} />} title="Member not found" />
        <EmptyState
          title="Member not found"
          description="This member may have been removed, or the link is no longer valid."
          actions={<Button onClick={() => navigate(MEMBERS_ROUTE)}>Back to Members</Button>}
        />
      </>
    );
  }

  const role = memberOrgRole(contact, admins);
  const state = accountState(contact);

  return (
    <>
      <PageHeader
        breadcrumbs={crumbs}
        icon={<UsersRound size={22} />}
        title={contact.contact_name || 'Member'}
        description={contact.contact_email || undefined}
        actions={
          <div className={styles.headPills}>
            <StatusPill tone={state.tone}>{state.label}</StatusPill>
            {contact.is_primary && (
              <Badge tone="primary" appearance="subtle" size="sm">
                Primary
              </Badge>
            )}
          </div>
        }
      />

      <div className={styles.cardStack}>
        {/* Account — the lifecycle control center (manage-only). */}
        {canManage && (
        <Card flat>
          <CardHeader>
            <CardTitle>Account</CardTitle>
          </CardHeader>
          <CardBody>
            <div className={styles.settingRows}>
              <SettingRow
                title="Sign-in status"
                description={contact.has_login ? 'This member can sign in.' : 'Directory contact — no login yet.'}
                control={
                  contact.has_login ? (
                    <GatedAction label="Disable" />
                  ) : (
                    <Button variant="primary" size="sm" onClick={() => setProvisioning(true)}>
                      Create account
                    </Button>
                  )
                }
              />
              <SettingRow
                title="Reset password"
                description="Email this member a password-reset link."
                control={<GatedAction label="Send reset link" />}
              />
              <SettingRow
                title="Two-factor authentication"
                description="Require an authenticator app on next sign-in."
                control={<GatedAction label="Require 2FA" />}
              />
            </div>
          </CardBody>
        </Card>
        )}

        {/* Details. */}
        <Card flat>
          <CardHeader
            actions={
              canManage ? (
                <div className={styles.headerActions}>
                  {!contact.is_primary && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={setPrimary.isPending}
                      onClick={() => setPrimary.mutate(contact.contact_id)}
                    >
                      Set as primary
                    </Button>
                  )}
                  <Button size="sm" variant="secondary" onClick={() => setEditOpen(true)}>
                    Edit
                  </Button>
                </div>
              ) : undefined
            }
          >
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardBody>
            <DescList>
              <DescItem label="Name">{contact.contact_name || '—'}</DescItem>
              <DescItem label="Email">{contact.contact_email || '—'}</DescItem>
              <DescItem label="Phone">
                {contact.contact_phone && contact.contact_phone !== '-' ? contact.contact_phone : '—'}
              </DescItem>
              <DescItem label="Position">{contact.contact_position || '—'}</DescItem>
              <DescItem label="Sign-in method">{contact.authentication_method || '—'}</DescItem>
            </DescList>
          </CardBody>
        </Card>

        {/* Org access — org role + effective permissions (rollup deferred). */}
        <Card flat>
          <CardHeader>
            <CardTitle>Organization access</CardTitle>
          </CardHeader>
          <CardBody>
            <DescList>
              <DescItem label="Org role">
                <Badge tone={memberRoleTone(role)} appearance="subtle" size="sm">
                  {role}
                </Badge>
              </DescItem>
            </DescList>
            <p className={styles.placeholder}>
              Team memberships and effective permissions will appear here once the Connections view ships.
            </p>
          </CardBody>
        </Card>

        {/* Danger zone. */}
        {canManage && !contact.is_primary && (
          <Card flat>
            <CardHeader>
              <CardTitle>Danger zone</CardTitle>
            </CardHeader>
            <CardBody>
              <SettingRow
                title="Remove from organization"
                description="Revoke this member's access. This cannot be undone."
                control={
                  <Button variant="danger" size="sm" onClick={() => setRemoving(true)}>
                    Remove
                  </Button>
                }
              />
            </CardBody>
          </Card>
        )}
      </div>

      <ContactDialog open={editOpen} onOpenChange={setEditOpen} contact={contact} />

      <ConfirmDialog
        open={provisioning}
        onOpenChange={setProvisioning}
        title="Create account?"
        description={`A login will be created for “${contact.contact_name}” and a set-password link emailed to ${contact.contact_email}.`}
        confirmLabel="Create account"
        loading={provision.isPending}
        onConfirm={() => provision.mutate(contact.contact_id)}
      />

      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        title="Remove member?"
        description={`“${contact.contact_name}” will be removed from your organization. This cannot be undone.`}
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate(contact.contact_id)}
      />
    </>
  );
}

export default PersonDetailPage;
